import { GMAPS_API_KEY } from 'constants/envs'
import { AdvancedMarker, APIProvider, Map, MapEvent } from '@vis.gl/react-google-maps'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import NewProducerMarker from './NewProducerMarker'
import { NewPositionsWithProducers, NewProducerMapProps, PopulatedLocation } from './types'

const NewProducerMap = ({
  defaultOpenSlug,
  locations,
  height = '100%',
  zoom = 8,
  center: conditionalCenter,
  enablePanning,
  onBoundsChange,
  fitBounds
}: NewProducerMapProps) => {
  // Constants
  const center = conditionalCenter || {
    lat: 50.943383,
    lng: 3.928225
  }

  // Local state
  const [popupPositionIndex, setPopupPositionIndex] = useState<number | undefined>(undefined)
  const [popupProducerSlug, setPopupProducerSlug] = useState<string | undefined>(defaultOpenSlug)
  const [activePositionIndex, setActivePositionIndex] = useState<number | undefined>(undefined)
  const [activeProducerLocation, setActiveProducerLocation] = useState<string | undefined>(undefined)
  const [centerLastSelectedProd, setCenterLastSelectedProd] = useState<{ lat: number; lng: number } | undefined>(center)

  // fitBounds triggers another tilesloaded event, so re-fitting on every one of them grows the viewport indefinitely
  const hasFittedBounds = useRef(false)

  useEffect(() => {
    hasFittedBounds.current = false
  }, [locations])

  // Kept stable across renders so the position objects keep their identity: @vis.gl re-assigns
  // AdvancedMarkerElement.position whenever it sees a new object, which throws inside the Maps SDK when that
  // element was detached from the map in the same commit
  const positionsWithProducerInfo = useMemo<NewPositionsWithProducers[]>(
    () =>
      locations
        .filter((loc) => loc.coordinates)
        .map((producer, index) => ({
          index,
          position: { lat: Number(producer.coordinates.lat), lng: Number(producer.coordinates.long) },
          producer
        })),
    [locations]
  )

  /**
   * Returns the sorted positions
   *
   * @returns PositionsWithProducers[]
   */
  const getSortedPositions = useCallback((): NewPositionsWithProducers[] => {
    // Sort & return the positionsWithproducers
    return positionsWithProducerInfo.slice().sort((a, b) => {
      if (
        (a.producer.slug === popupProducerSlug && a.index === popupPositionIndex) ||
        (a.producer.slug === activeProducerLocation && a.index === activePositionIndex)
      ) {
        return 1
      }

      if (
        (b.producer.slug === popupProducerSlug && b.index === popupPositionIndex) ||
        (b.producer.slug === activeProducerLocation && b.index === activePositionIndex)
      ) {
        return -1
      }

      return 0
    })
  }, [activePositionIndex, activeProducerLocation, positionsWithProducerInfo, popupPositionIndex, popupProducerSlug])

  /**
   * Centers the locations on the map
   *
   * @param {MapEvent} e
   * @param {PopulatedLocation[]} locations
   */
  const centerLocationsOnMap = (e: MapEvent, locations: PopulatedLocation[]) => {
    const mapBounds = new google.maps.LatLngBounds()
    let hasBounds = false

    locations.forEach(({ coordinates }) => {
      const lat = Number(coordinates?.lat)
      const lng = Number(coordinates?.long)

      if (Number.isFinite(lat) && Number.isFinite(lng)) {
        mapBounds.extend({ lat, lng })
        hasBounds = true
      }
    })

    if (!hasBounds) return

    hasFittedBounds.current = true
    e.map.fitBounds(mapBounds)
  }

  return (
    <div style={{ height, width: '100%' }}>
      <APIProvider apiKey={GMAPS_API_KEY}>
        <Map
          mapId="newProducerMap"
          style={{ width: '100%' }}
          defaultCenter={centerLastSelectedProd}
          defaultZoom={zoom}
          gestureHandling={enablePanning ? 'greedy' : 'cooperative'}
          disableDefaultUI={true}
          scrollwheel={false}
          disableDoubleClickZoom={true}
          zoomControl={true}
          minZoom={8}
          maxZoom={15}
          fullscreenControl={false}
          onClick={() => {
            setPopupProducerSlug(undefined)
            setPopupPositionIndex(undefined)
          }}
          onBoundsChanged={(e) => onBoundsChange && onBoundsChange(e.detail.bounds)}
          onTilesLoaded={(e) => fitBounds && !hasFittedBounds.current && centerLocationsOnMap(e, locations)}
        >
          {getSortedPositions().map(({ position, index, producer }) => {
            if (Number.isFinite(position.lat) && Number.isFinite(position.lng)) {
              return (
                <AdvancedMarker key={index} position={position} anchorLeft="-50%" anchorTop="-100%" clickable>
                  <NewProducerMarker
                    key={`${producer.id}${index}`}
                    location={producer}
                    lat={position.lat}
                    lng={position.lng}
                    index={index}
                    onMouseEnter={(producer, index) => {
                      setActiveProducerLocation(producer.slug)
                      setActivePositionIndex(index)
                    }}
                    onMouseLeave={() => {
                      setActiveProducerLocation(undefined)
                      setActivePositionIndex(undefined)
                    }}
                    onClick={(producer) => {
                      setCenterLastSelectedProd({ lat: Number(producer.coordinates.lat), lng: Number(producer.coordinates.long) })
                      setPopupProducerSlug(producer.slug)
                    }}
                    active={activeProducerLocation === producer.slug && activePositionIndex === index}
                    popup={popupProducerSlug === producer.slug}
                  />
                </AdvancedMarker>
              )
            }

            return null
          })}
        </Map>
      </APIProvider>
    </div>
  )
}

export default NewProducerMap
